import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";

// Define coming soon specialties outside component to avoid ESLint warning
const COMING_SOON_SPECIALTIES = [
  "Family Medicine",
  "Pediatrics", 
  "Psychiatry",
  "Neurology"
];

function SpecialtySelection() {
  const [specialties, setSpecialties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { hasSpecialtyAccess } = useAuth();

  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const snapshot = await getDocs(collection(db, "specialties"));
        const uploadedSpecialties = snapshot.docs.map((doc) => ({
          name: doc.data().name,
          isAvailable: true
        }));

        // Create array of all specialties (uploaded + coming soon)
        const allSpecialties = [...uploadedSpecialties];
        
        // Add coming soon specialties that aren't already uploaded
        COMING_SOON_SPECIALTIES.forEach(specialty => {
          const isAlreadyUploaded = uploadedSpecialties.some(
            uploaded => uploaded.name.toLowerCase() === specialty.toLowerCase()
          );
          
          if (!isAlreadyUploaded) {
            allSpecialties.push({
              name: specialty,
              isAvailable: false
            });
          }
        });

        // Sort alphabetically by name
        allSpecialties.sort((a, b) => a.name.localeCompare(b.name));
        setSpecialties(allSpecialties);
      } catch (err) {
        console.error("Error fetching specialties:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpecialties();
  }, []);

  const handleSpecialtyClick = (specialty) => {
    if (specialty.isAvailable) {
      navigate(`/table/${encodeURIComponent(specialty.name)}`);
    }
  };

  const handleBackClick = () => {
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-600 border-t-gray-500 dark:border-t-gray-400 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[15px] text-gray-500 dark:text-gray-400">Loading specialties...</p>
          </div>
        </div>
      </div>
    );
  }

  if (specialties.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="space-y-6 max-w-[1600px] mx-auto">
          <div>
            <button
              onClick={handleBackClick}
              className="inline-flex items-center px-4 py-2 text-[13px] font-medium text-black dark:text-white bg-white dark:bg-gray-700 border border-black/15 dark:border-gray-600 rounded-full hover:border-black/25 dark:hover:border-gray-500 hover:bg-black/[0.02] dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-black/[0.04] dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-[20px] font-semibold text-black dark:text-white mb-2 tracking-[-0.02em]">No Specialties Available</h3>
            <p className="text-[15px] text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Please check back later for available specialties.</p>
          </div>
        </div>
      </div>
    );
  }

  const availableCount = specialties.filter(s => s.isAvailable).length;
  const comingSoonCount = specialties.filter(s => !s.isAvailable).length;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Back Button */}
      <div>
        <button
          onClick={handleBackClick}
          className="inline-flex items-center px-4 py-2 text-[13px] font-medium text-black dark:text-white bg-white dark:bg-gray-700 border border-black/15 dark:border-gray-600 rounded-full hover:border-black/25 dark:hover:border-gray-500 hover:bg-black/[0.02] dark:hover:bg-gray-600 transition-colors duration-200"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* Header */}
      <div className="mb-2">
        <h1 className="text-[20px] font-semibold text-black dark:text-white mb-1 tracking-[-0.02em]">Available Specialties</h1>
        <p className="text-[14px] text-gray-500 dark:text-gray-300">Choose a specialty to explore residency programs and opportunities</p>
      </div>

      {/* Specialties Grid */}
      <div className="bg-white dark:bg-gray-700/80 rounded-2xl border border-black/8 dark:border-gray-600 overflow-hidden">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {specialties.map((specialty, index) => {
              const hasAccess = hasSpecialtyAccess(specialty.name);

              return (
                <button
                  key={index}
                  onClick={() => handleSpecialtyClick(specialty)}
                  disabled={!specialty.isAvailable}
                  className={`text-left p-5 rounded-xl border transition-all duration-200 relative ${
                    specialty.isAvailable
                      ? "border-black/8 dark:border-gray-600 hover:border-black/20 dark:hover:border-gray-500 bg-white dark:bg-gray-700 hover:bg-black/[0.02] dark:hover:bg-gray-600 group cursor-pointer"
                      : "border-black/6 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-700 cursor-not-allowed opacity-90 dark:opacity-90"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold text-[15px] ${
                      specialty.isAvailable ? "text-black dark:text-white" : "text-gray-400 dark:text-gray-500"
                    }`}>
                      {specialty.name}
                    </span>

                    {specialty.isAvailable ? (
                      <div className="flex items-center space-x-2">
                        {hasAccess && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-black dark:bg-blue-600 text-white uppercase tracking-wide">
                            Unlocked
                          </span>
                        )}
                        <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-black/60 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-400 border border-black/10 dark:border-gray-600 ml-3 flex-shrink-0">
                        Coming Soon
                      </span>
                    )}
                  </div>

                  {!specialty.isAvailable && (
                    <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-2">
                      This specialty will be available soon
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Available Specialties Card - subtle blue gradient */}
        <div className="bg-gradient-to-r from-blue-50/70 to-blue-100/40 dark:from-gray-700 dark:to-gray-800 rounded-2xl border border-black/8 dark:border-gray-600 border-l-2 border-l-blue-400/40 dark:border-l-blue-400/50 p-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500/10 dark:bg-blue-400/20 rounded-xl mb-4">
              <svg className="w-6 h-6 text-blue-600/80 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white mb-2 tracking-[-0.01em]">Available Now</h3>
            <p className="text-3xl font-semibold text-blue-600/90 dark:text-blue-400 mb-1">{availableCount}</p>
            <p className="text-[13px] text-gray-600 dark:text-gray-400">specialties ready to explore</p>
          </div>
        </div>

        {/* Coming Soon Card - monochrome */}
        <div className="bg-black/[0.02] dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-600 p-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-black/5 dark:bg-gray-700 rounded-xl mb-4">
              <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-[15px] font-semibold text-black dark:text-white mb-2 tracking-[-0.01em]">Coming Soon</h3>
            <p className="text-3xl font-semibold text-black dark:text-white mb-1">{comingSoonCount}</p>
            <p className="text-[13px] text-gray-500 dark:text-gray-300">specialties in development</p>
          </div>
        </div>
      </div>

      {/* Info Banner - monochrome with subtle blue tint */}
      {comingSoonCount > 0 && (
        <div className="bg-gradient-to-r from-blue-50/30 to-transparent dark:from-gray-800 dark:to-transparent rounded-2xl border border-black/8 dark:border-gray-600 p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-10 h-10 bg-black/5 dark:bg-white/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-[15px] font-semibold text-black dark:text-white mb-1 tracking-[-0.01em]">More Specialties Coming Soon</h3>
              <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
                We're actively working on adding {COMING_SOON_SPECIALTIES.join(", ")} and other specialties.
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default SpecialtySelection;